import { forwardRef } from "react";

import { SxProps, Theme, styled, useTheme } from "@mui/material";

import { NextLink, NextLinkProps } from "@/components/NextLink/NextLink";
import { FormIcon, FormIconProps } from "@/forms-design-system/FormIcon/FormIcon";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";
import { ArrowDiagonalRight } from "@/icons/outlined";

export type FormLinkBlockProps = Omit<NextLinkProps, "as"> & {
  sx?: SxProps<Theme>;
  components?: {
    icon?: {
      size?: FormIconProps["size"];
      color?: FormIconProps["color"];
      sx?: SxProps<Theme>;
    };
  };
};

const StyledLink = styled(NextLink)(({ theme }) => ({
  color: theme.palette.formsComponents.linkBlock.textColor,
}));

export const FormLinkBlock = forwardRef<HTMLAnchorElement, FormLinkBlockProps>(
  ({ children, sx, components, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.linkBlock || !theme.palette.formsComponents?.global) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        ["&&"]: {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          padding: 1,
          borderRadius: theme.radius(1),
          ...theme.typography.body2,
          color: theme.palette.formsComponents.linkBlock.textColor,
          backgroundColor: theme.palette.formsComponents.linkBlock.backgroundColor,
          outlineColor: "transparent",
          transition: "background-color 200ms ease-out, outline 100ms ease-out",
          boxSizing: "border-box",
          ["&:hover"]: {
            backgroundColor: theme.palette.formsComponents.linkBlock.hoverBackgroundColor,
            ["& .form-link-block-icon"]: {
              opacity: 1,
            },
          },
          ["&:active, &:focus-visible"]: {
            outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
          },
        },
      },
      sx
    );

    return (
      <StyledLink {...rest} ref={ref} sx={styles}>
        {children}
        <FormIcon
          className="form-link-block-icon"
          size={components?.icon?.size ?? "small"}
          color={components?.icon?.color ?? theme.palette.formsComponents.linkBlock.iconColor}
          sx={mergeSx({ opacity: 0, transition: "opacity 200ms ease-out" }, components?.icon?.sx)}
        >
          <ArrowDiagonalRight />
        </FormIcon>
      </StyledLink>
    );
  }
);

FormLinkBlock.displayName = "FormLinkBlock";
